import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  Divider,
  Card,
  Statistic,
  message,
} from "antd";
import type { TableProps } from "antd";
import request from "../../../utils/request";
import {
  TokenLogItem,
  TokenLogsStats,
  TokenUserRankItem,
  TabProps,
  TokenLogsResponse,
} from "../../../types/dataControl";
import ActionTypeTag from "../../TokenUsage/ActionTypeTag";

const { RangePicker } = DatePicker;
const { Option } = Select;

const TokenLogsTab: React.FC<TabProps> = ({ API_BASE_URL, token }) => {
  const [tokenLogs, setTokenLogs] = useState<TokenLogItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [stats, setStats] = useState<TokenLogsStats | null>(null);
  const [userRank, setUserRank] = useState<TokenUserRankItem[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  const [form] = Form.useForm();

  // 获取Token消耗记录
  const fetchTokenLogs = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      const values = form.getFieldsValue();
      const params: any = {
        page: pagination.current,
        page_size: pagination.pageSize,
      };

      // 添加筛选条件
      if (values.open_id) params.open_id = values.open_id;
      if (values.action) params.action = values.action;
      if (values.project_id) params.project_id = values.project_id;

      // 处理时间范围
      if (values.dateRange && values.dateRange[0] && values.dateRange[1]) {
        params.start_time = values.dateRange[0].format("YYYY-MM-DD");
        params.end_time = values.dateRange[1].format("YYYY-MM-DD");
      }

      const response = await request.get<{
        code: number;
        msg: string;
        data: TokenLogsResponse;
      }>(`${API_BASE_URL}/api/v3/admin/token/logs`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setTokenLogs(response.data.data.list);
        setTotal(response.data.data.total);
      } else {
        message.error(response.data.msg || "获取Token消耗记录失败");
      }
    } catch (error) {
      console.error("获取Token消耗记录失败:", error);
      message.error("获取Token消耗记录失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, token, form, pagination]);

  // 获取Token消耗统计
  const fetchTokenStats = useCallback(async () => {
    if (!token) return;

    try {
      const values = form.getFieldsValue();
      const params: any = {};

      // 处理时间范围
      if (values.dateRange && values.dateRange[0] && values.dateRange[1]) {
        params.start_time = values.dateRange[0].format("YYYY-MM-DD");
        params.end_time = values.dateRange[1].format("YYYY-MM-DD");
      }

      const response = await request.get<{
        code: number;
        msg: string;
        data: TokenLogsStats;
      }>(`${API_BASE_URL}/api/v3/admin/token/stats`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error("获取Token统计失败:", error);
    }
  }, [API_BASE_URL, token, form]);

  // 获取用户Token消耗排行
  const fetchUserRank = useCallback(async () => {
    if (!token) return;

    try {
      const values = form.getFieldsValue();
      const params: any = {
        limit: 10,
      };

      // 处理时间范围
      if (values.dateRange && values.dateRange[0] && values.dateRange[1]) {
        params.start_time = values.dateRange[0].format("YYYY-MM-DD");
        params.end_time = values.dateRange[1].format("YYYY-MM-DD");
      }

      const response = await request.get<{
        code: number;
        msg: string;
        data: TokenUserRankItem[];
      }>(`${API_BASE_URL}/api/v3/admin/token/rank`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setUserRank(response.data.data);
      }
    } catch (error) {
      console.error("获取用户排行失败:", error);
    }
  }, [API_BASE_URL, token, form]);

  // 初始加载数据
  useEffect(() => {
    fetchTokenLogs();
    fetchTokenStats();
    fetchUserRank();
  }, [fetchTokenLogs, fetchTokenStats, fetchUserRank]);

  // 处理表格变化
  const handleTableChange: TableProps<TokenLogItem>["onChange"] = (
    pagination
  ) => {
    setPagination({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 20,
    });
  };

  // 处理查询
  const handleSearch = () => {
    setPagination({
      ...pagination,
      current: 1,
    });
    fetchTokenLogs();
    fetchTokenStats();
    fetchUserRank();
  };

  // 重置筛选
  const handleReset = () => {
    form.resetFields();
    setPagination({
      current: 1,
      pageSize: 20,
    });
    fetchTokenLogs();
    fetchTokenStats();
    fetchUserRank();
  };

  // 定义表格列
  const columns: TableProps<TokenLogItem>["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
    },
    {
      title: "用户信息",
      key: "user",
      width: 180,
      render: (_, record) => (
        <div>
          <div>UID: {record.open_id}</div>
          <div style={{ fontSize: 12, color: "#888" }}>
            {record.user_email || ""}
          </div>
        </div>
      ),
    },
    {
      title: "项目",
      key: "project",
      width: 150,
      render: (_, record) => (
        <div>
          <div>{record.project_name || "-"}</div>
          <div style={{ fontSize: 12, color: "#888" }}>
            ID: {record.project_id || "-"}
          </div>
        </div>
      ),
    },
    {
      title: "操作类型",
      dataIndex: "action",
      key: "action",
      width: 120,
      render: (action: string) => <ActionTypeTag action={action} />,
    },
    {
      title: "Token变化",
      key: "token_change",
      width: 150,
      render: (_, record) => {
        const isRecharge = record.action === "recharge";
        const displayValue = Math.abs(record.token_used);

        return (
          <div>
            <div>
              {isRecharge ? (
                <span style={{ color: "#52c41a", fontWeight: "bold" }}>
                  +{displayValue.toLocaleString()}
                </span>
              ) : (
                <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
                  -{displayValue.toLocaleString()}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#888" }}>
              余额: {record.token_after.toLocaleString()}
            </div>
          </div>
        );
      },
    },
    {
      title: "模型",
      dataIndex: "model_name",
      key: "model_name",
      width: 150,
      render: (value: string) => value || "-",
    },
    {
      title: "Token明细",
      key: "token_breakdown",
      width: 180,
      render: (_, record) => {
        const totalTokens = record.total_tokens || record.token_used || record.content_size || 0;
        return (
          <div>
            <div>总计: {totalTokens.toLocaleString()}</div>
            <div style={{ fontSize: 12, color: "#888" }}>
              输入: {(record.prompt_tokens || 0).toLocaleString()} / 输出: {(record.completion_tokens || 0).toLocaleString()}
            </div>
          </div>
        );
      },
    },
    {
      title: "成本(元)",
      dataIndex: "cost",
      key: "cost",
      width: 110,
      render: (value?: number) => (value ? value.toFixed(4) : "0"),
    },
    {
      title: "AgentID",
      dataIndex: "agent_id",
      key: "agent_id",
      width: 100,
      render: (id: number) => id || "-",
    },
    {
      title: "摘要ID",
      dataIndex: "summary_id",
      key: "summary_id",
      width: 100,
      render: (id: number) => id || "-",
    },
    {
      title: "创建时间",
      dataIndex: "created_at",
      key: "created_at",
      width: 170,
      render: (date: string) => new Date(date).toLocaleString("zh-CN"),
    },
  ];

  // 用户排行表格列
  const rankColumns: TableProps<TokenUserRankItem>["columns"] = [
    {
      title: "排名",
      key: "rank",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "用户ID",
      dataIndex: "open_id",
      key: "open_id",
      width: 100,
    },
    {
      title: "邮箱",
      dataIndex: "user_email",
      key: "user_email",
      render: (email: string) => email || "-",
    },
    {
      title: "总消耗",
      dataIndex: "total_token_used",
      key: "total_token_used",
      width: 120,
      render: (value: number) => (
        <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
          {value.toLocaleString()}
        </span>
      ),
    },
    {
      title: "操作次数",
      dataIndex: "operation_count",
      key: "operation_count",
      width: 100,
    },
    {
      title: "平均消耗",
      dataIndex: "avg_token_used",
      key: "avg_token_used",
      width: 120,
      render: (value: number) => value.toLocaleString(),
    },
  ];

  return (
    <>
      {/* 统计概览 */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="总消耗Token"
                value={stats.total_token_used}
                prefix={
                  <i className="ri-coin-line" style={{ color: "#ff4d4f" }} />
                }
                formatter={(value) => value?.toLocaleString()}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="消耗用户数"
                value={stats.total_users}
                prefix={
                  <i className="ri-user-line" style={{ color: "#1677ff" }} />
                }
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="总操作次数"
                value={stats.total_operations}
                prefix={
                  <i
                    className="ri-function-line"
                    style={{ color: "#52c41a" }}
                  />
                }
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="人均消耗"
                value={stats.avg_token_per_user}
                prefix={
                  <i
                    className="ri-bar-chart-line"
                    style={{ color: "#fa8c16" }}
                  />
                }
                formatter={(value) => value?.toLocaleString()}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 用户消耗排行 */}
      <Card title="用户Token消耗排行TOP10" style={{ marginBottom: 16 }}>
        <Table
          columns={rankColumns}
          dataSource={userRank}
          rowKey="open_id"
          pagination={false}
          size="small"
        />
      </Card>

      {/* Token消耗记录筛选表单 */}
      <Form form={form} layout="vertical" onFinish={handleSearch}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="用户ID" name="open_id">
              <Input placeholder="请输入用户ID" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="操作类型" name="action">
              <Select placeholder="请选择操作类型" allowClear>
                <Option value="summary">摘要生成</Option>
                <Option value="chat">普通对话</Option>
                <Option value="chat_kb_mcp">MCP对话</Option>
                <Option value="chat_kb">知识库对话</Option>
                <Option value="hotword">热词生成</Option>
                <Option value="search">向量搜索</Option>
                <Option value="recharge">Token充值</Option>
                <Option value="recommend">AI推荐</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="项目ID" name="project_id">
              <Input placeholder="请输入项目ID" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={6}>
            <Form.Item label="时间范围" name="dateRange">
              <RangePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} lg={24}>
            <Form.Item label=" " colon={false}>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={
                    <i className="ri-search-line" style={{ marginRight: 4 }} />
                  }
                >
                  查询
                </Button>
                <Button
                  onClick={handleReset}
                  icon={
                    <i className="ri-refresh-line" style={{ marginRight: 4 }} />
                  }
                >
                  重置
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Divider style={{ margin: "12px 0 24px" }} />

      {/* Token消耗记录列表表格 */}
      <Table
        columns={columns}
        dataSource={tokenLogs}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
      />
    </>
  );
};

export default TokenLogsTab;
